// Win evaluation helpers kept separate from rendering for clarity.

export const createSymbolKey = (paytableMap) => (alias) =>
  paytableMap._keyFromAlias ? paytableMap._keyFromAlias(alias || '') : null;

// Adjacent-line evaluation: walks each payline left-to-right (grid column 0 onward) and counts touching matches/wilds.
// Rules enforced:
// - Must start at grid position 0 (leftmost reel).
// - Symbols must be contiguous along the payline (already adjacent by design).
// - Wilds substitute until a base symbol is found; all-wilds pay as wild.
// - Scatters break the run.
export const evalLinesAdjacent = ({ grid, payLines, paytableMap, bet, multiplier, symbolKey }) => {
  const wins = [];
  payLines.forEach((line, idx) => {
    // Traverse left-to-right, starting at column 0.
    const coords = line.map((row, col) => ({ col, row }));

    let target = null;
    let count = 0;

    for (let k = 0; k < coords.length; k++) {
      const { col, row } = coords[k];
      const alias = grid[col][row];
      if (alias === 'scatter') break;
      const key = symbolKey(alias);

      if (!target) {
        // Wild defers choosing the base symbol until we meet a non-wild.
        if (alias === 'wild') {
          count += 1;
          continue;
        }
        target = key;
        count += 1;
        continue;
      }

      if (alias === 'wild' || symbolKey(alias) === target) {
        count += 1;
      } else {
        break;
      }
    }

    // If all were wilds, pay as wild (if present in paytable).
    if (!target) {
      const found = coords.find(({ col, row }) => {
        const a = grid[col][row];
        return a !== 'wild' && a !== 'scatter';
      });
      target = found ? symbolKey(grid[found.col][found.row]) : 'wild';
    }

    if (!target) return;
    if (count >= 3) {
      const payout = paytableMap[target]?.[count] || 0;
      if (payout > 0) {
        const matchedCoords = coords.slice(0, count);
        wins.push({
          line: idx + 1,
          key: target,
          count,
          payout,
          lineWin: payout * bet * multiplier,
          coords: matchedCoords,
        });
      }
    }
  });
  return wins;
};

// Scatter-style evaluation: counts matching symbols anywhere along each line.
export const evalLinesScattered = ({ grid, payLines, paytableMap, revealSymbol, bet, multiplier }) => {
  const wins = [];
  payLines.forEach((line, idx) => {
    const coords = [];
    let count = 0;
    line.forEach((row, col) => {
      if (grid[col][row] === revealSymbol) { coords.push({ col, row }); count += 1; }
    });
    if (count >= 3) {
      const payout = paytableMap[revealSymbol]?.[count] || 0;
      if (payout > 0) {
        wins.push({
          line: idx + 1,
          key: revealSymbol,
          count,
          payout,
          lineWin: payout * bet * multiplier,
          coords,
        });
      }
    }
  });
  return wins;
};

export const countScatters = (grid) => grid.flat().filter((s) => s === 'scatter').length;
